import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/app/store/auth'
import api from '@/lib/api/axios'
import { appLocale } from '@/lib/dateUtils'

interface SyncLog {
    id: number
    status: string
    modelsFound: number
    modelsAdded: number
    modelsRemoved: number
    modelsUpdated: number
    errorMessage: string | null
    startedAt: string
    completedAt: string | null
    triggeredBy: string
    provider: { id: number; name: string; displayName: string } | null
}

export function SyncHistoryTab() {
    const { t } = useTranslation()
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated)

    const { data, isLoading } = useQuery({
        queryKey: ['ai-sync-logs'],
        queryFn: async () => {
            const res = await api.get('/api/ai/models/sync/logs', { params: { limit: 30 } })
            return res.data
        },
        enabled: isAuthenticated,
    })

    const logs: SyncLog[] = data?.logs || []

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-lg font-semibold">{t('aiModels.syncHistory.title', 'Sync History')}</h2>
                <p className="text-sm text-muted-foreground">{t('aiModels.syncHistory.description', 'Log of all model sync operations')}</p>
            </div>

            {isLoading ? (
                <div className="text-sm text-muted-foreground py-8 text-center">{t('common.loading', 'Loading...')}</div>
            ) : logs.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center">{t('aiModels.syncHistory.empty', 'No sync history yet')}</div>
            ) : (
                <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b bg-muted/30">
                                <th className="text-left py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiModels.syncHistory.columns.time', 'Time')}</th>
                                <th className="text-left py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiModels.syncHistory.columns.provider', 'Provider')}</th>
                                <th className="text-left py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiModels.syncHistory.columns.status', 'Status')}</th>
                                <th className="text-right py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiModels.syncHistory.columns.found', 'Found')}</th>
                                <th className="text-right py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiModels.syncHistory.columns.added', 'Added')}</th>
                                <th className="text-right py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiModels.syncHistory.columns.removed', 'Removed')}</th>
                                <th className="text-right py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiModels.syncHistory.columns.updated', 'Updated')}</th>
                                <th className="text-left py-2.5 px-4 text-xs font-medium text-muted-foreground uppercase tracking-wider">{t('aiModels.syncHistory.columns.triggered', 'Triggered')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map((log) => (
                                <>
                                    <tr key={log.id} className="border-b border-border/50 hover:bg-muted/20">
                                        <td className="py-2.5 px-4 whitespace-nowrap">
                                            {new Date(log.startedAt).toLocaleDateString(appLocale(), { month: 'short', day: 'numeric' })},{' '}
                                            {new Date(log.startedAt).toLocaleTimeString(appLocale(), { hour: '2-digit', minute: '2-digit' })}
                                        </td>
                                        <td className="py-2.5 px-4">{log.provider?.displayName || t('common.all', 'All')}</td>
                                        <td className="py-2.5 px-4">
                                            {log.status === 'completed' && (
                                                <span className="text-green-600 dark:text-green-400 text-xs font-medium">
                                                    <span className="mr-1">&#10003;</span>{t('aiModels.syncHistory.status.completed', 'Completed')}
                                                </span>
                                            )}
                                            {log.status === 'failed' && (
                                                <span className="text-red-600 dark:text-red-400 text-xs font-medium">
                                                    <span className="mr-1">&#10007;</span>{t('aiModels.syncHistory.status.failed', 'Failed')}
                                                </span>
                                            )}
                                            {log.status === 'running' && (
                                                <span className="text-blue-600 dark:text-blue-400 text-xs font-medium">{t('aiModels.syncHistory.status.running', 'Running...')}</span>
                                            )}
                                            {log.status !== 'completed' && log.status !== 'failed' && log.status !== 'running' && (
                                                <span className="text-yellow-600 dark:text-yellow-400 text-xs font-medium">
                                                    <span className="mr-1">&#9888;</span>{t('aiModels.syncHistory.status.partial', 'Partial')}
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-2.5 px-4 text-right">{log.modelsFound}</td>
                                        <td className="py-2.5 px-4 text-right">
                                            {log.modelsAdded > 0 ? <span className="text-green-600 dark:text-green-400">+{log.modelsAdded}</span> : <span className="text-muted-foreground">0</span>}
                                        </td>
                                        <td className="py-2.5 px-4 text-right">
                                            {log.modelsRemoved > 0 ? <span className="text-red-600 dark:text-red-400">-{log.modelsRemoved}</span> : <span className="text-muted-foreground">0</span>}
                                        </td>
                                        <td className="py-2.5 px-4 text-right">
                                            {log.modelsUpdated > 0 ? <span className="text-yellow-600 dark:text-yellow-400">~{log.modelsUpdated}</span> : <span className="text-muted-foreground">0</span>}
                                        </td>
                                        <td className="py-2.5 px-4">
                                            <Badge variant="outline" className="text-[10px] uppercase">
                                                {log.triggeredBy}
                                            </Badge>
                                        </td>
                                    </tr>
                                    {/* Error message row */}
                                    {log.errorMessage && (
                                        <tr key={`${log.id}-err`}>
                                            <td colSpan={8} className="px-4 py-2">
                                                <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 rounded px-3 py-1.5 font-mono">
                                                    {log.errorMessage}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
